import React from "react";
import {
  MapPin,
  Star,
  Eye,
  Share2,
  RefreshCw,
  Settings,
  BadgeAlert,
  MessageCircle,
  Copy,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { ListGridProps } from "../../../types";
import { useLocation } from "react-router-dom";
import { timeAgo } from "../../../helpers/function.helper";
import {
  copyProfileId,
  getGenderPlaceholder,
} from "../../../helpers/matches.helper";
import { UPLOAD_URL } from "../../../utils/constants";
import { useLanguage } from "../../../context/LanguageContext";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";

export const ListView: React.FC<ListGridProps> = ({
  matches,
  setProfiles,
  onViewProfile,
  onAddToShortlist,
  onSwitchProfile,
  onShareProfile,
  onReportProfile,
  onManageProfile,
  getExtraActions,
  onTakeProfile,
  onWhatsAppProfile,
  renderActions,
}) => {
  const location = useLocation();
  const isOtherPage = location.pathname.includes("other");
  const isDirectPage = location.pathname.includes("/direct");
  const { language, t } = useLanguage();
  const scoreLabel = t("matches.labels.score");
  const publicLabel = t("matches.labels.public");
  const privateLabel = t("matches.laebels.private");
  const viewsTemplate = t("matches.labels.viewsCount");
  const lastSeenPrefix = t("matches.labels.lastSeen");
  const createdByLabel = t("matches.labels.createdBy");
  const viewProfileLabel = t("matches.buttons.viewProfile");
  const switchLabel = t("matches.buttons.switch");
  const manageProfileLabel = t("matches.buttons.manageProfile");
  const manageLabel = t("matches.buttons.manage");
  const shareLabel = t("matches.buttons.share");
  const reportLabel = t("matches.buttons.report");
  const takeProfileLabel = t("Take This Profile");
  return (
    <div className="space-y-4">
      {matches.map((match) => {
        const lastSeenLabel = `${lastSeenPrefix} ${timeAgo(
          match.lastSeenAt,
          language
        )}`;
        const isSuspended =
          String(match.status ?? "").toLowerCase() === "suspended";
        const viewsLabel = viewsTemplate.replace(
          "{count}",
          String(match.views ?? 0)
        );
        const heightDisplay = match.height;
        const handleCardKeyDown = (
          event: React.KeyboardEvent<HTMLDivElement>
        ) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onViewProfile?.(match);
          }
        };

        const isPrivate = match.visibleToAll === "false";
        const blurPhoto = shouldBlurProfileImage(match.imagePrivacy);
        return (
          <div
            key={match.maritalId}
            className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden"
          >
            {isSuspended && (
              <div className="px-4 py-2 text-xs font-semibold text-red-700 bg-red-50 border-b border-red-100">
                Profile is suspended
              </div>
            )}
            <div className="flex flex-col sm:hidden">
              <div className="flex items-center gap-4 mb-2 px-4 pt-3 ">
                <span className="text-[8px] font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                  {scoreLabel}: {match.profileScore}%
                </span>

                <div
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${
                    isPrivate ? "bg-gray-200" : "bg-green-100"
                  }`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${
                      isPrivate ? "bg-gray-500" : "bg-green-600"
                    }`}
                  />
                  <span
                    className={`text-[8px] font-semibold ${
                      isPrivate ? "text-gray-700" : "text-green-700"
                    }`}
                  >
                    {isPrivate ? t("Private") : publicLabel}
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-green-600">
                    <Eye size={10} />
                    <span className="text-[8px] font-semibold">{viewsLabel}</span>
                  </div>
                </div>
              </div>
              <hr className="py-1 text-gray-700" />
              <div className="flex items-start px-5 ">
                <div>
                  <h3 className="text-xs sm:text-base font-semibold text-gray-900 leading-tight truncate">
                    {match.fullName}
                  </h3>
                  <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                    <span className="truncate">{match.maritalId}</span>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void copyProfileId(match.maritalId);
                      }}
                      aria-label="Copy profile ID"
                      title="Copy profile ID"
                      className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-purple-600"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                    <span className="text-gray-300">|</span>
                    <span className="truncate">{lastSeenLabel}</span>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="relative flex flex-row items-start gap-3 sm:gap-4 p-3 sm:p-4 cursor-pointer transition hover:bg-purple-50/40 focus:outline-none"
              onClick={() => onViewProfile?.(match)}
              role="button"
              tabIndex={0}
              onKeyDown={handleCardKeyDown}
            >
              {/* LEFT IMAGE */}
              <div
                className="
                 relative 
                    w-24 h-24            /* 📱 Base (mobile) */
                    sm:w-32 sm:h-48      /* 🧩 Small screens (≥640px) */
                    md:w-48 md:h-48      /* 💻 Medium screens (≥768px) */
                    lg:w-64 lg:h-64      /* 🖥 Large screens (≥1024px) */
                    xl:w-72 xl:h-72      /* 🖥️ Extra-large screens (≥1280px) */
                    rounded-xl 
                    overflow-hidden 
                    flex-shrink-0
              "
              >
                <img
                  src={UPLOAD_URL + "/users/profile_pic/" + match.image}
                  alt={match.fullName}
                  className={`w-full h-full object-cover object-top ${
                    blurPhoto ? "blur-sm scale-110 brightness-90" : ""
                  }`}
                  onError={(event) => {
                    (event.currentTarget as HTMLImageElement).src =
                      getGenderPlaceholder(match.gender);
                  }}
                />
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddToShortlist?.(match, matches, setProfiles);
                  }}
                  aria-label="Shortlist profile"
                  className="absolute top-2 left-2 flex items-center justify-center rounded-full bg-white/90 p-1.5 shadow-md text-orange-500 hover:bg-white"
                >
                  {match.isShortlisted ? (
                    <Star className="w-4 h-4" fill="currentColor" />
                  ) : (
                    <Star className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* RIGHT DETAILS */}
              <div className="flex-1 min-w-0 flex flex-col relative w-full">
                {/* TOP ROW: name + more icon */}
                <div className="hidden sm:flex items-center gap-4 mb-3">
                  <span className="text-[8px] font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                    {scoreLabel}: {match.profileScore}%
                  </span>
                  <div
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${
                      isPrivate ? "bg-gray-200" : "bg-green-100"
                    }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full ${
                        isPrivate ? "bg-gray-500" : "bg-green-600"
                      }`}
                    />
                    <span
                      className={`text-xs font-semibold ${
                        isPrivate ? "text-gray-700" : "text-green-700"
                      }`}
                    >
                      {isPrivate ? privateLabel : publicLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 text-green-600">
                      <Eye size={16} />
                      <span className="text-sm font-semibold">
                        {viewsLabel}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="items-start hidden sm:flex">
                  <div>
                    <h3 className="text-xs sm:text-base font-semibold text-gray-900 leading-tight truncate">
                      {match.fullName}
                    </h3>
                    <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                      <span className="truncate">{match.maritalId}</span>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          void copyProfileId(match.maritalId);
                        }}
                        aria-label="Copy profile ID"
                        title="Copy profile ID"
                        className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-purple-600"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      <span className="text-gray-300">|</span>
                      <span className="truncate">{lastSeenLabel}</span>
                    </div>
                  </div>
                </div>

                {/* DETAILS BELOW */}
                <div className="mt-2 text-[10px] text-gray-600 leading-relaxed space-y-1">
                  <p className="flex flex-wrap gap-x-2 gap-y-1">
                    {match.maritalStatus && <span>{match.maritalStatus}</span>}
                    <span>
                      {createdByLabel}: {match.createdBy}
                    </span>
                  </p>
                  <p className="flex flex-wrap gap-x-2 gap-y-1">
                    <span>{match.age}</span>
                    {heightDisplay && <span>{heightDisplay}</span>}
                    {match.caste && <span>{match.caste}</span>}
                  </p>
                  <p className="flex flex-wrap gap-x-2 gap-y-1">
                    {match.education && <span>{match.education}</span>}
                    {match.occupation && <span>{match.occupation}</span>}
                    {match.annualIncome && <span>{match.annualIncome}</span>}
                  </p>
                  {match.location && (
                    <p className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-purple-500" />
                      <span className="truncate">{match.location}</span>
                    </p>
                  )}
                </div>

                {/* DESKTOP BUTTONS */}
                <div className="hidden sm:flex flex-wrap gap-2 justify-start pt-2">
                  {renderActions ? (
                    renderActions(match)
                  ) : isDirectPage ? (
                    <button
                      className="flex items-center gap-2 py-2 px-4 rounded-lg bg-green-600 text-sm font-semibold text-white hover:bg-green-700 transition"
                      onClick={(event) => {
                        event.stopPropagation();
                        onTakeProfile?.(match);
                      }}
                    >
                      <MessageCircle size={18} />
                      <span>{takeProfileLabel}</span>
                    </button>
                  ) : (
                    <>
                  <button
                    className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                    onClick={(event) => {
                      event.stopPropagation();
                      onViewProfile(match);
                    }}
                  >
                    <Eye size={18} />
                    <span className="hidden sm:flex">{viewProfileLabel}</span>
                  </button>
                  {!isOtherPage && (
                    <>
                      <div className="relative">
                        <button
                          className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSwitchProfile(match);
                          }}
                        >
                          <RefreshCw size={14} />
                          <span className="hidden sm:flex">{switchLabel}</span>
                        </button>

                        {match?.switchCount ? (
                          <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-red-500 text-[10px] font-semibold text-white">
                            {match?.switchCount}
                          </span>
                        ) : null}
                      </div>

                      <button
                        className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                        onClick={(event) => {
                          event.stopPropagation();
                          onManageProfile(match);
                        }}
                      >
                        <Settings size={14} />
                        <span className="hidden sm:flex">
                          {manageProfileLabel}
                        </span>
                      </button>
                      {onWhatsAppProfile && (
                        <button
                          className="flex items-center gap-2 py-2 px-4 rounded-lg border border-green-200 text-sm font-semibold text-green-600 hover:bg-green-50 transition"
                          onClick={(event) => {
                            event.stopPropagation();
                            onWhatsAppProfile(match);
                          }}
                        >
                          <FaWhatsapp size={16} />
                          <span className="hidden sm:flex">WhatsApp</span>
                        </button>
                      )}
                    </>
                  )}
                  <button
                    className="flex items-center gap-2 py-2 px-4 rounded-lg border border-purple-200 text-sm font-semibold text-purple-600 hover:bg-purple-50 transition"
                    onClick={(event) => {
                      event.stopPropagation();
                      onShareProfile?.(match);
                    }}
                  >
                    <Share2 size={20} />
                    <span className="hidden sm:flex">{shareLabel}</span>
                  </button>
                  {isOtherPage && (
                    <button
                      className="px-4 py-2 border border-gray-300 bg-red-400 rounded-lg flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm hover:bg-red-500 text-white transition"
                      onClick={(event) => {
                        event.stopPropagation();
                        onReportProfile?.(match);
                      }}
                    >
                      <BadgeAlert size={14} />
                      <span className="hidden sm:flex">{reportLabel}</span>
                    </button>
                  )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* MOBILE BOTTOM BAR */}
            {renderActions ? (
              <div className="border-t border-gray-100 md:hidden p-2">
                {renderActions(match)}
              </div>
            ) : isDirectPage ? (
              <div className="border-t border-gray-100 md:hidden">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onTakeProfile?.(match);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>{takeProfileLabel}</span>
                </button>
              </div>
            ) : isOtherPage ? (
              <div className="border-t border-gray-100 md:hidden">
                <div className="grid grid-cols-2 divide-x">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onShareProfile?.(match);
                    }}
                    className="flex items-center justify-center gap-2 py-3 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>{shareLabel}</span>
                  </button>
                  <button
                    className="px-4 py-2 border border-gray-300 bg-red-400 rounded-lg flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm hover:bg-red-500 text-white transition"
                    onClick={(event) => {
                      event.stopPropagation();
                      onReportProfile?.(match);
                    }}
                  >
                    <BadgeAlert className="w-4 h-4" />
                    <span>{reportLabel}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="border-t border-gray-100 md:hidden">
                <div className="grid grid-cols-2 gap-2 p-2">
                  <div className="relative">
                    <button
                      type="button"
                      className={`flex w-full min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-3 text-[11px] font-semibold transition ${"text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSwitchProfile?.(match);
                      }}
                    >
                      <RefreshCw className="w-4 h-4 shrink-0" />
                      <span className="truncate">{switchLabel}</span>
                      {match?.switchCount ? (
                        <span className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[18px] h-5 px-1 rounded-full bg-red-500 text-[10px] font-semibold text-white">
                          {match?.switchCount}
                        </span>
                      ) : null}
                    </button>
                  </div>

                  <button
                    type="button"
                    className={`flex w-full min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-3 text-[11px] font-semibold transition ${"text-white bg-gradient-to-r from-orange-400 to-pink-500 hover:from-orange-500 hover:to-pink-600"}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onManageProfile(match);
                    }}
                  >
                    <Settings className="w-4 h-4 shrink-0" fill={"none"} />
                    <span className="truncate">{manageLabel}</span>
                  </button>
                  {/* 🔗 Share */}
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onShareProfile?.(match);
                    }}
                    className="flex w-full min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-3 text-[11px] font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition"
                  >
                    <Share2 className="w-4 h-4 shrink-0" />
                    <span className="truncate">{shareLabel}</span>
                  </button>
                  {onWhatsAppProfile && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onWhatsAppProfile(match);
                      }}
                      className="flex w-full min-w-0 items-center justify-center gap-1 rounded-xl px-2 py-3 text-[11px] font-semibold text-white bg-green-500 hover:bg-green-600 transition"
                    >
                      <FaWhatsapp className="w-4 h-4 shrink-0" />
                      <span className="truncate">WhatsApp</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
